import { useEffect, useState } from 'react';
import { Box, Stack, Typography, CircularProgress, Chip } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';
import axios from '../../utils/axios';

// Date + time — FDate from defaultValues is date-only, and "when exactly did
// this change" is the point of the history view.
const formatWhen = (d: string) =>
  new Date(d).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

// One docHistory row as the backend returns it (GET /v1/documents/
// user-docs/:docType/history and /v1/documents/admin-docs/history/:docType).
// Summary-scale by design: section titles, changed fields, and brief capped
// excerpts of added/removed text — never full content.
interface HistoryChange {
  title: string | null;
  // 1-based position, present when the section has no title.
  position?: number;
}
interface HistoryEdit extends HistoryChange {
  fields: string[];
  previousTitle?: string;
  snippets?: { added?: string[]; removed?: string[] };
}
export interface DocHistoryEntry {
  id: number;
  actorId: number | null;
  actorName: string | null;
  actorRole: string | null;
  created: string;
  changes: {
    added?: HistoryChange[];
    edited?: HistoryEdit[];
    deleted?: HistoryChange[];
    reordered?: boolean;
  };
}

const FIELD_LABELS: Record<string, string> = {
  title: 'title',
  content: 'content',
  jobsiteQuestion: 'job-site question',
};

const sectionName = (c: HistoryChange | string | null) => {
  if (typeof c === 'string' || c === null) return `"${c && c.length > 0 ? c : 'Untitled section'}"`;
  if (c.title && c.title.length > 0) return `"${c.title}"`;
  return c.position ? `Section ${c.position}` : '"Untitled section"';
};

// Flat render list: a main line per change, with optional +/− excerpt
// sub-lines under content edits.
interface ChangeLine {
  text: string;
  kind: 'main' | 'add' | 'remove';
}

function changeLines(changes: DocHistoryEntry['changes']): ChangeLine[] {
  const lines: ChangeLine[] = [];
  (changes.edited || []).forEach((e) => {
    const fields = (e.fields || []).map((f) => FIELD_LABELS[f] || f).join(', ');
    const renamed = e.previousTitle ? `, renamed from ${sectionName(e.previousTitle)}` : '';
    lines.push({
      text: `Edited ${sectionName(e)}${fields ? ` (${fields}${renamed})` : ''}`,
      kind: 'main',
    });
    (e.snippets?.added || []).forEach((s) => lines.push({ text: s, kind: 'add' }));
    (e.snippets?.removed || []).forEach((s) => lines.push({ text: s, kind: 'remove' }));
  });
  (changes.added || []).forEach((a) => lines.push({ text: `Added ${sectionName(a)}`, kind: 'main' }));
  (changes.deleted || []).forEach((d) => lines.push({ text: `Removed ${sectionName(d)}`, kind: 'main' }));
  if (changes.reordered) lines.push({ text: 'Reordered sections', kind: 'main' });
  return lines;
}

// Theme-aware diff colours: a translucent tint of the semantic colour works on
// both light and dark backgrounds (the fixed `lighter` pastels glare in dark
// mode), with the text shade flipped for contrast.
const snippetSx = {
  add: (theme: Theme) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
    bgcolor: alpha(theme.palette.success.main, 0.14),
  }),
  remove: (theme: Theme) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
    bgcolor: alpha(theme.palette.error.main, 0.14),
    textDecoration: 'line-through',
  }),
} as const;

// Newest-first edit history for a document editor's History tab. Fetches on
// mount; `url` should point at one of the doc-history endpoints, with the
// optional `params` (e.g. { userId }) forwarded as the query string.
export default function DocHistoryList({
  url,
  params,
}: {
  url: string;
  params?: Record<string, unknown>;
}) {
  const [entries, setEntries] = useState<DocHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setEntries(null);
    setError(null);
    axios
      .get(url, { params })
      .then((r) => {
        if (!active) return;
        const data = Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
        setEntries(data);
      })
      .catch((e) => {
        if (active) setError(e?.message || 'Failed to load history');
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, JSON.stringify(params || {})]);

  if (error) {
    return (
      <Typography sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>{error}</Typography>
    );
  }
  if (entries === null) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }
  if (entries.length === 0) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 6, color: 'text.secondary' }}>
        <HistoryIcon />
        <Typography variant="body2">No changes recorded yet.</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ py: 1 }}>
      {entries.map((entry) => (
        <Box
          key={entry.id}
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2">{entry.actorName || 'Unknown user'}</Typography>
            {entry.actorRole && (
              <Chip size="small" label={entry.actorRole} sx={{ textTransform: 'capitalize' }} />
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto !important' }}>
              {formatWhen(entry.created)}
            </Typography>
          </Stack>
          {changeLines(entry.changes).map((line, idx) =>
            line.kind === 'main' ? (
              // eslint-disable-next-line react/no-array-index-key
              <Typography key={idx} variant="body2" sx={{ color: 'text.secondary' }}>
                • {line.text}
              </Typography>
            ) : (
              <Typography
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                variant="body2"
                sx={(theme) => ({
                  ml: 2.5,
                  px: 0.75,
                  my: 0.25,
                  borderRadius: 0.5,
                  width: 'fit-content',
                  ...snippetSx[line.kind as 'add' | 'remove'](theme),
                })}
              >
                {line.kind === 'add' ? '+' : '−'} {line.text}
              </Typography>
            ),
          )}
        </Box>
      ))}
    </Stack>
  );
}
